export type Tool =
  | "select"
  | "polyline"
  | "polygon"
  | "rect"
  | "circle"
  | "freehand"
  | "text"
  | "marker"
  | "probe"
  | "measure"
  | "eraser"

export type MarkerSymbol = "circle" | "rectangle" | "diamond"

export type LineEndSymbol = "none" | "arrow" | "filled_arrow" | "double_arrows" | "filled_square" | "filled_circle" | "losange" | "chevron_right" | "chevron_left" | "x" | "circle" | "square" | "filled_losange"

export type LineBodyStyle = "simple" | "double"

export type LineStyle = "solid" | "dotted" | "dashed" | "dashdot"

export const LINE_STYLE_DASHES: Record<LineStyle, number[]> = {
  solid: [],
  dotted: [2, 4],
  dashed: [8, 4],
  dashdot: [8, 4, 2, 4],
}

export interface Point {
  x: number
  y: number
}

export interface DrawObject {
  id: string
  layerId: string
  type: "line" | "polyline" | "polygon" | "rect" | "circle" | "freehand" | "text" | "marker" | "probe" | "measure"
  points: Point[] // For probe: points[0] = head (tip), points[1] = tail
  color: string
  strokeWidth: number
  text?: string
  comment?: string
  closed?: boolean
  lineStyle?: LineStyle
  commentOffset?: Point
  commentRotation?: number // degrees rotation for comment box
  visible?: boolean // object-level visibility
  // Text formatting
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  // Line end symbols (for polyline, freehand)
  headSymbol?: LineEndSymbol
  tailSymbol?: LineEndSymbol
  // Fill for closed shapes (polygon, rect, circle)
  filled?: boolean
  fillOpacity?: number // 0-100, default 50
  // Line body style
  lineBodyStyle?: LineBodyStyle
  // General rotation for objects (degrees)
  rotation?: number
  // Net assignment for PCB connectivity
  net?: string
  // Marker-specific properties
  markerSize?: number // 4-20 pixels
  markerSymbol?: MarkerSymbol // circle, rectangle, diamond
  markerFilled?: boolean
  // Probe-specific properties
  probeSize?: number // 45-180 pixels (length of probe body, default 90)
}

export interface Layer {
  id: string
  name: string
  color: string
  visible: boolean
  locked: boolean
  opacity: number
  objects: DrawObject[]
}

export interface ProjectProperties {
  projectName: string
  brand: string
  ecuName: string
  pcbVersion: string
  comment: string
}

export const DEFAULT_PROJECT_PROPERTIES: ProjectProperties = {
  projectName: "Untitled",
  brand: "",
  ecuName: "",
  pcbVersion: "",
  comment: "",
}

export interface PCBProject {
  layers: Layer[]
  activeLayerId: string | null
  activeTool: Tool
  activeColor: string
  strokeWidth: number
  activeLineStyle: LineStyle
  imageGrayscale: number
  imageTint: number // 0-360 hue rotation
  imageOverlay: string // hex color for color overlay
  imageOverlayOpacity: number // 0-100
  pcbImage: string | null
  zoom: number
  panOffset: Point
  projectProperties: ProjectProperties
}

export const LAYER_COLORS = [
  "#22c55e",
  "#ef4444",
  "#3b82f6",
  "#eab308",
  "#a855f7",
  "#f97316",
  "#06b6d4",
  "#ec4899",
]

export const PRESET_COLORS = [
  "#22c55e",
  "#ef4444",
  "#3b82f6",
  "#eab308",
  "#a855f7",
  "#f97316",
  "#06b6d4",
  "#ec4899",
]

// ---- JSON Serialization Schema ----

export interface JSONObjectLine {
  color: string
  strokeWidth: number
  start: [number, number]
  end: [number, number]
  comment?: string
  lineStyle?: string
  commentOffset?: [number, number]
  commentRotation?: number
  visible?: boolean
  headSymbol?: string
  tailSymbol?: string
  lineBodyStyle?: string
  net?: string
}

export interface JSONObjectPolyline {
  color: string
  strokeWidth: number
  points: [number, number][]
  closed?: boolean
  comment?: string
  lineStyle?: string
  commentOffset?: [number, number]
  commentRotation?: number
  visible?: boolean
  headSymbol?: string
  tailSymbol?: string
  lineBodyStyle?: string
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  net?: string
}

export interface JSONObjectPolygon {
  color: string
  strokeWidth: number
  points: [number, number][]
  comment?: string
  lineStyle?: string
  commentOffset?: [number, number]
  commentRotation?: number
  visible?: boolean
  filled?: boolean
  fillOpacity?: number
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  net?: string
}

export interface JSONObjectCircle {
  color: string
  strokeWidth: number
  center: [number, number]
  radiusPoint: [number, number]
  comment?: string
  lineStyle?: string
  commentOffset?: [number, number]
  commentRotation?: number
  visible?: boolean
  filled?: boolean
  fillOpacity?: number
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  net?: string
}

export interface JSONObjectRectangle {
  color: string
  strokeWidth: number
  topLeft: [number, number]
  bottomRight: [number, number]
  comment?: string
  lineStyle?: string
  commentOffset?: [number, number]
  commentRotation?: number
  visible?: boolean
  filled?: boolean
  fillOpacity?: number
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  net?: string
}

export interface JSONObjectFreehand {
  color: string
  strokeWidth: number
  points: [number, number][]
  comment?: string
  lineStyle?: string
  commentOffset?: [number, number]
  commentRotation?: number
  visible?: boolean
  headSymbol?: string
  tailSymbol?: string
  lineBodyStyle?: string
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  net?: string
}



export interface JSONObjectMeasure {
  color: string
  strokeWidth: number
  start: [number, number]
  end: [number, number]
  comment?: string
  visible?: boolean
  net?: string
}

export interface JSONObjectText {
  color: string
  text: string
  position: [number, number]
  fontSize: number
  comment?: string
  fontFamily?: string
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  rotation?: number
}

export interface JSONObjectMarker {
  color: string
  position: [number, number]
  size: number // 4-20
  symbol: string // circle, rectangle, diamond
  filled: boolean
  comment?: string
  commentOffset?: [number, number]
  commentRotation?: number
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  visible?: boolean
  net?: string
}

export interface JSONObjectProbe {
  color: string
  head: [number, number] // tip position
  tail: [number, number] // tail position (for rotation)
  size: number // 10-50, probe body length
  comment?: string
  commentOffset?: [number, number]
  commentRotation?: number
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  fontUnderline?: boolean
  visible?: boolean
  net?: string
}

export interface JSONLayerData {
  title: string
  color: string
  visible: boolean
  locked: boolean
  opacity: number
  lines: Record<string, JSONObjectLine>
  polylines: Record<string, JSONObjectPolyline>
  polygons: Record<string, JSONObjectPolygon>
  circles: Record<string, JSONObjectCircle>
  rectangles: Record<string, JSONObjectRectangle>
  freehand: Record<string, JSONObjectFreehand>
  measures: Record<string, JSONObjectMeasure>
  texts: Record<string, JSONObjectText>
  markers: Record<string, JSONObjectMarker>
  probes: Record<string, JSONObjectProbe>
}

export interface PCBProjectJSON {
  pcbImage: string | null
  projectProperties?: ProjectProperties
  imageGrayscale?: number
  imageTint?: number
  imageOverlay?: string
  imageOverlayOpacity?: number
  zoom?: number
  panOffset?: [number, number]
  [layerKey: string]: JSONLayerData | string | number | null | undefined | ProjectProperties | [number, number]
}

// ---- Serialization Functions ----

function ptToArr(p: Point): [number, number] {
  return [Math.round(p.x * 100) / 100, Math.round(p.y * 100) / 100]
}

function arrToPt(a: [number, number]): Point {
  return { x: a[0], y: a[1] }
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9) + Date.now().toString(36)
}

export function serializeProject(
  layers: Layer[],
  pcbImage: string | null,
  projectProperties?: ProjectProperties,
  imageGrayscale?: number,
  imageTint?: number,
  imageOverlay?: string,
  imageOverlayOpacity?: number,
  zoom?: number,
  panOffset?: Point,
): PCBProjectJSON {
  const json: PCBProjectJSON = {
    pcbImage,
    projectProperties: projectProperties ?? DEFAULT_PROJECT_PROPERTIES,
    imageGrayscale: imageGrayscale ?? 0,
    imageTint: imageTint ?? 0,
    imageOverlay: imageOverlay ?? "",
    imageOverlayOpacity: imageOverlayOpacity ?? 0,
    zoom: zoom != null ? Math.round(zoom * 1000) / 1000 : undefined,
    panOffset: panOffset ? [Math.round(panOffset.x * 100) / 100, Math.round(panOffset.y * 100) / 100] : undefined,
  }

  layers.forEach((layer, idx) => {
    const key = `layer${idx + 1}`
    const data: JSONLayerData = {
      title: layer.name,
      color: layer.color,
      visible: layer.visible,
      locked: layer.locked,
      opacity: layer.opacity,
      lines: {},
      polylines: {},
      polygons: {},
      circles: {},
      rectangles: {},
      freehand: {},
      measures: {},
      texts: {},
      markers: {},
      probes: {},
    }

    layer.objects.forEach((obj, oi) => {
      const objKey = `${obj.type}${oi + 1}`
      const cOff = obj.commentOffset && (obj.commentOffset.x !== 0 || obj.commentOffset.y !== 0) ? ptToArr(obj.commentOffset) : undefined
      switch (obj.type) {
        case "line":
          data.lines[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            start: ptToArr(obj.points[0]),
            end: ptToArr(obj.points[1]),
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            net: obj.net,
          }
          break
        case "polyline":
          data.polylines[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            points: obj.points.map(ptToArr),
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible, headSymbol: obj.headSymbol, tailSymbol: obj.tailSymbol, lineBodyStyle: obj.lineBodyStyle,
            rotation: obj.rotation, net: obj.net,
          }
          break
        case "polygon":
          data.polygons[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            points: obj.points.map(ptToArr),
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible, filled: obj.filled, fillOpacity: obj.fillOpacity, lineBodyStyle: obj.lineBodyStyle,
            rotation: obj.rotation, net: obj.net,
          }
          break
        case "circle": {
          const cx = obj.points[0].x
          const cy = obj.points[0].y
          const dx = obj.points[1].x - cx
          const dy = obj.points[1].y - cy
          const radius = Math.sqrt(dx * dx + dy * dy)
          data.circles[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            center: ptToArr(obj.points[0]),
            radius: Math.round(radius * 100) / 100,
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible, filled: obj.filled, fillOpacity: obj.fillOpacity, lineBodyStyle: obj.lineBodyStyle,
            rotation: obj.rotation, net: obj.net,
          }
          break
        }
        case "rect":
          data.rectangles[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            topLeft: ptToArr({
              x: Math.min(obj.points[0].x, obj.points[1].x),
              y: Math.min(obj.points[0].y, obj.points[1].y),
            }),
            bottomRight: ptToArr({
              x: Math.max(obj.points[0].x, obj.points[1].x),
              y: Math.max(obj.points[0].y, obj.points[1].y),
            }),
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible, filled: obj.filled, fillOpacity: obj.fillOpacity, lineBodyStyle: obj.lineBodyStyle,
            rotation: obj.rotation, net: obj.net,
          }
          break
        case "freehand":
          data.freehand[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            points: obj.points.map(ptToArr),
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible, headSymbol: obj.headSymbol, tailSymbol: obj.tailSymbol, lineBodyStyle: obj.lineBodyStyle,
            rotation: obj.rotation, net: obj.net,
          }
          break
        
        case "measure":
          data.measures[objKey] = {
            color: obj.color,
            width: obj.strokeWidth,
            start: ptToArr(obj.points[0]),
            end: ptToArr(obj.points[1]),
            comment: obj.comment,
            lineStyle: obj.lineStyle !== "solid" ? obj.lineStyle : undefined,
            commentOffset: cOff,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            net: obj.net,
          }
          break
        case "text":
          data.texts[objKey] = {
            color: obj.color,
            text: obj.text ?? "",
            position: ptToArr(obj.points[0]),
            fontSize: obj.fontSize ?? Math.max(12, obj.strokeWidth * 6),
            fontFamily: obj.fontFamily,
            fontBold: obj.fontBold,
            fontItalic: obj.fontItalic,
            fontUnderline: obj.fontUnderline,
            rotation: obj.rotation,
          }
          break
        case "marker":
          data.markers[objKey] = {
            color: obj.color,
            position: ptToArr(obj.points[0]),
            size: obj.markerSize ?? 12,
            symbol: obj.markerSymbol ?? "circle",
            filled: obj.markerFilled ?? true,
            comment: obj.comment,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible,
            net: obj.net,
          }
          break
        case "probe":
          data.probes[objKey] = {
            color: obj.color,
            head: ptToArr(obj.points[0]),
            tail: ptToArr(obj.points[1]),
            size: obj.probeSize ?? 90,
            comment: obj.comment,
            commentOffset: cOff,
            commentRotation: obj.commentRotation,
            fontFamily: obj.fontFamily, fontSize: obj.fontSize, fontBold: obj.fontBold, fontItalic: obj.fontItalic, fontUnderline: obj.fontUnderline,
            visible: obj.visible,
            net: obj.net,
          }
          break
        
      }
    })

    json[key] = data
  })

  return json
}

export function deserializeProject(json: PCBProjectJSON): {
  layers: Layer[]; pcbImage: string | null;
  projectProperties?: ProjectProperties; imageGrayscale?: number; imageTint?: number;
  imageOverlay?: string; imageOverlayOpacity?: number;
  zoom?: number; panOffset?: Point;
} {
  const layers: Layer[] = []
  const pcbImage = (json.pcbImage as string | null) ?? null
  const projectProperties = json.projectProperties ?? undefined
  const imageGrayscale = typeof json.imageGrayscale === "number" ? json.imageGrayscale : undefined
  const imageTint = typeof json.imageTint === "number" ? json.imageTint : undefined
  const imageOverlay = typeof json.imageOverlay === "string" ? json.imageOverlay : undefined
  const imageOverlayOpacity = typeof json.imageOverlayOpacity === "number" ? json.imageOverlayOpacity : undefined
  const zoom = typeof json.zoom === "number" ? json.zoom : undefined
  const panOffsetArr = json.panOffset
  const panOffset = Array.isArray(panOffsetArr) ? { x: panOffsetArr[0], y: panOffsetArr[1] } : undefined

  for (const key of Object.keys(json)) {
    if (key === "pcbImage") continue
    const data = json[key] as JSONLayerData
    if (!data || typeof data !== "object" || !data.title) continue

    const layerId = generateId()
    const objects: DrawObject[] = []

    if (data.lines) {
      for (const [, line] of Object.entries(data.lines)) {
        objects.push({
          id: generateId(), layerId, type: "line",
          color: line.color, strokeWidth: line.width,
          points: [arrToPt(line.start), arrToPt(line.end)],
          comment: line.comment,
          lineStyle: line.lineStyle ?? "solid",
          commentOffset: line.commentOffset ? arrToPt(line.commentOffset) : undefined,
          fontFamily: line.fontFamily, fontSize: line.fontSize, fontBold: line.fontBold, fontItalic: line.fontItalic, fontUnderline: line.fontUnderline,
          net: line.net,
        })
      }
    }

    if (data.polylines) {
      for (const [, pl] of Object.entries(data.polylines)) {
        objects.push({
          id: generateId(), layerId, type: "polyline",
          color: pl.color, strokeWidth: pl.width,
          points: pl.points.map(arrToPt),
          comment: pl.comment,
          lineStyle: pl.lineStyle ?? "solid",
          commentOffset: pl.commentOffset ? arrToPt(pl.commentOffset) : undefined,
          commentRotation: pl.commentRotation,
          fontFamily: pl.fontFamily, fontSize: pl.fontSize, fontBold: pl.fontBold, fontItalic: pl.fontItalic, fontUnderline: pl.fontUnderline,
          visible: pl.visible, headSymbol: pl.headSymbol, tailSymbol: pl.tailSymbol, lineBodyStyle: pl.lineBodyStyle,
          rotation: pl.rotation, net: pl.net,
        })
      }
    }

    if (data.polygons) {
      for (const [, pg] of Object.entries(data.polygons)) {
        objects.push({
          id: generateId(), layerId, type: "polygon",
          color: pg.color, strokeWidth: pg.width,
          points: pg.points.map(arrToPt),
          closed: true,
          comment: pg.comment,
          lineStyle: pg.lineStyle ?? "solid",
          commentOffset: pg.commentOffset ? arrToPt(pg.commentOffset) : undefined,
          commentRotation: pg.commentRotation,
          fontFamily: pg.fontFamily, fontSize: pg.fontSize, fontBold: pg.fontBold, fontItalic: pg.fontItalic, fontUnderline: pg.fontUnderline,
          visible: pg.visible, filled: pg.filled, fillOpacity: pg.fillOpacity, lineBodyStyle: pg.lineBodyStyle,
          rotation: pg.rotation, net: pg.net,
        })
      }
    }

    if (data.circles) {
      for (const [, c] of Object.entries(data.circles)) {
        const center = arrToPt(c.center)
        objects.push({
          id: generateId(), layerId, type: "circle",
          color: c.color, strokeWidth: c.width,
          points: [center, { x: center.x + c.radius, y: center.y }],
          comment: c.comment,
          lineStyle: c.lineStyle ?? "solid",
          commentOffset: c.commentOffset ? arrToPt(c.commentOffset) : undefined,
          commentRotation: c.commentRotation,
          fontFamily: c.fontFamily, fontSize: c.fontSize, fontBold: c.fontBold, fontItalic: c.fontItalic, fontUnderline: c.fontUnderline,
          visible: c.visible, filled: c.filled, fillOpacity: c.fillOpacity, lineBodyStyle: c.lineBodyStyle,
          rotation: c.rotation, net: c.net,
        })
      }
    }

    if (data.rectangles) {
      for (const [, r] of Object.entries(data.rectangles)) {
        objects.push({
          id: generateId(), layerId, type: "rect",
          color: r.color, strokeWidth: r.width,
          points: [arrToPt(r.topLeft), arrToPt(r.bottomRight)],
          comment: r.comment,
          lineStyle: r.lineStyle ?? "solid",
          commentOffset: r.commentOffset ? arrToPt(r.commentOffset) : undefined,
          commentRotation: r.commentRotation,
          fontFamily: r.fontFamily, fontSize: r.fontSize, fontBold: r.fontBold, fontItalic: r.fontItalic, fontUnderline: r.fontUnderline,
          visible: r.visible, filled: r.filled, fillOpacity: r.fillOpacity, lineBodyStyle: r.lineBodyStyle,
          net: r.net,
        })
      }
    }

    if (data.freehand) {
      for (const [, f] of Object.entries(data.freehand)) {
        objects.push({
          id: generateId(), layerId, type: "freehand",
          color: f.color, strokeWidth: f.width,
          points: f.points.map(arrToPt),
          comment: f.comment,
          lineStyle: f.lineStyle ?? "solid",
          commentOffset: f.commentOffset ? arrToPt(f.commentOffset) : undefined,
          commentRotation: f.commentRotation,
          fontFamily: f.fontFamily, fontSize: f.fontSize, fontBold: f.fontBold, fontItalic: f.fontItalic, fontUnderline: f.fontUnderline,
          visible: f.visible, headSymbol: f.headSymbol, tailSymbol: f.tailSymbol, lineBodyStyle: f.lineBodyStyle,
          net: f.net,
        })
      }
    }

    

    if (data.measures) {
      for (const [, m] of Object.entries(data.measures)) {
        objects.push({
          id: generateId(), layerId, type: "measure",
          color: m.color, strokeWidth: m.width,
          points: [arrToPt(m.start), arrToPt(m.end)],
          comment: m.comment,
          lineStyle: m.lineStyle ?? "solid",
          commentOffset: m.commentOffset ? arrToPt(m.commentOffset) : undefined,
          fontFamily: m.fontFamily, fontSize: m.fontSize, fontBold: m.fontBold, fontItalic: m.fontItalic, fontUnderline: m.fontUnderline,
          net: m.net,
        })
      }
    }

    if (data.texts) {
      for (const [, t] of Object.entries(data.texts)) {
        objects.push({
          id: generateId(), layerId, type: "text",
          color: t.color, strokeWidth: Math.round(t.fontSize / 6),
          points: [arrToPt(t.position)],
          text: t.text,
          fontSize: t.fontSize,
          fontFamily: t.fontFamily,
          fontBold: t.fontBold,
          fontItalic: t.fontItalic,
          fontUnderline: t.fontUnderline,
        })
      }
    }

    if (data.markers) {
      for (const [, m] of Object.entries(data.markers)) {
        objects.push({
          id: generateId(), layerId, type: "marker",
          color: m.color, strokeWidth: 1,
          points: [arrToPt(m.position)],
          markerSize: m.size ?? 12,
          markerSymbol: (m.symbol as MarkerSymbol) ?? "circle",
          markerFilled: m.filled ?? true,
          comment: m.comment,
          commentOffset: m.commentOffset ? arrToPt(m.commentOffset) : undefined,
          commentRotation: m.commentRotation,
          fontFamily: m.fontFamily, fontSize: m.fontSize, fontBold: m.fontBold, fontItalic: m.fontItalic, fontUnderline: m.fontUnderline,
          visible: m.visible,
          net: m.net,
        })
      }
    }

    if (data.probes) {
      for (const [, p] of Object.entries(data.probes)) {
        objects.push({
          id: generateId(), layerId, type: "probe",
          color: p.color, strokeWidth: 1,
          points: [arrToPt(p.head), arrToPt(p.tail)],
          probeSize: p.size ?? 90,
          comment: p.comment,
          commentOffset: p.commentOffset ? arrToPt(p.commentOffset) : undefined,
          commentRotation: p.commentRotation,
          fontFamily: p.fontFamily, fontSize: p.fontSize, fontBold: p.fontBold, fontItalic: p.fontItalic, fontUnderline: p.fontUnderline,
          visible: p.visible,
          net: p.net,
        })
      }
    }

    layers.push({
      id: layerId,
      name: data.title,
      color: data.color ?? LAYER_COLORS[layers.length % LAYER_COLORS.length],
      visible: data.visible ?? true,
      locked: data.locked ?? false,
      opacity: data.opacity ?? 1,
      objects,
    })
  }

  return { layers, pcbImage, projectProperties, imageGrayscale, imageTint, imageOverlay, imageOverlayOpacity, zoom, panOffset }
}
